import { Campaign } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import Input from '@vromo/ui/components/Input';
import Modal, { ModalActions, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';
import { action } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import React, { ChangeEvent, memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { update } from 'serializr';
import { qaLabelCampaigns } from '../../../../common-qa-labels/campaigns';

interface Props {
    isOpen: boolean;
    create: (campaign: Campaign) => void;
    onCancel: () => void;
}

const NewCampaign = observer(({ isOpen, create, onCancel }: Props) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isLoading, setLoading] = useState(false);
    const store = useLocalObservable(() => ({
        campaign: new Campaign(),
        updateName(e: ChangeEvent<HTMLInputElement>) {
            store.campaign.name = e.target.value;
        }
    }));

    const onNameChange = (e: ChangeEvent<HTMLInputElement>) =>
        (store.campaign.name = e.target.value);

    const onSave = async () => {
        setLoading(true);
        await create(store.campaign);
        setLoading(false);
    };

    const onKeyboardSubmit = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            onSave();
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        onNameChange({ target: { value: '' } });
        requestAnimationFrame(() => {
            inputRef?.current?.focus();
        });
    }, [isOpen]);

    useEffect(
        action(() => {
            update(store.campaign, {
                name: '',
                theme: {},
                promotion: {},
                link: {},
                widgets: []
            });
        }),
        []
    );

    return (
        <Modal isOpen={isOpen} onRequestClose={onCancel}>
            <ModalHeader>
                <Title>{t('campaign.newCampaignTitle')}</Title>
            </ModalHeader>

            <ModalContent>
                <label style={{ width: '100%' }}>
                    {t('campaign.name')}
                    <Input
                        ref={inputRef}
                        data-testid={qaLabelCampaigns.campaignNameInput}
                        type="text"
                        onChange={onNameChange}
                        onKeyDown={onKeyboardSubmit}
                    />
                </label>
            </ModalContent>
            <ModalActions>
                <Button onClick={onCancel}>{t('campaign.cancel')}</Button>
                <Button
                    data-test-id={qaLabelCampaigns.addCampaignModalSaveButton}
                    data-testid={qaLabelCampaigns.addCampaignModalSaveButton}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={onSave}>
                    {t('campaign.save')}
                </Button>
            </ModalActions>
        </Modal>
    );
});

export default memo(NewCampaign);
